<script>
    import CheckBoxAreaForInvoicesComponent from "./CheckBoxAreaForInvoicesComponent.svelte";
    import { onMount } from 'svelte';
    import CheckBoxAreForClassesComponent from "./CheckBoxAreForClassesComponent.svelte";
    import CompletedPaymentsList from './CompletedPaymentsListComponent.svelte';
    import NavBarReusable from "./NavbarReusable.svelte";
    import { push } from 'svelte-spa-router';
    
    // Student selection variables
    let allStudents = [];
    let selectedStudentId = null;
    let selectedStudent = null;
    let isLoadingStudents = true;
    let studentsError = null;
    let studentQuery = '';
    let filteredStudents = [];
    let showStudentDropdown = false;
    
    // Invoice page variables (identical to StandaloneInvoicesPage.svelte)
    let selectedMonths = [];
    let pdfUrl = ''; // Store the URL of the PDF to display
    let isCreatingPDF = false;
    let email = '';
    let pdfBase64V = '';

    let invoiceSum = 0; // Only set after invoice is generated
    let paymentLink = '';
    let isFetchingPaymentLink = false;

    let selectedClasses = [];

    let invoiceData = null;
    let errorForInvoiceData = null;
    let isLoadingInvoiceData = false; // Don't load automatically
    let backLinkUrl = "/admin"; // Back to admin page

    // Month filtering variables
    let loadedMonths = []; // Array of month strings like "2024-09"
    let originalMonth = ''; // Track the original month that was first loaded
    let isLoadingMonth = false;
    let monthlyData = {}; // Store data by month key

    // Helper: Get current month string
    function getCurrentMonthString() {
        const now = new Date();
        return `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
    }

    // Helper: Get next month string
    function getNextMonth(monthString) {
        const [year, month] = monthString.split('-').map(Number);
        const nextMonth = month === 12 ? 1 : month + 1;
        const nextYear = month === 12 ? year + 1 : year;
        return `${nextYear}-${nextMonth.toString().padStart(2, '0')}`;
    }

    // Helper: Get previous month string
    function getPreviousMonth(monthString) {
        const [year, month] = monthString.split('-').map(Number);
        const prevMonth = month === 1 ? 12 : month - 1;
        const prevYear = month === 1 ? year - 1 : year;
        return `${prevYear}-${prevMonth.toString().padStart(2, '0')}`;
    }

    // Helper: Format month for display
    function formatMonthForDisplay(monthString) {
        const [year, month] = monthString.split('-');
        const date = new Date(year, month - 1);
        return date.toLocaleDateString('lt-LT', { year: 'numeric', month: 'long' });
    }

    // Check if "Add Past Month" button should be disabled
    // Reactive computation based on loadedMonths
    $: canAddPast = (() => {
        if (loadedMonths.length === 0) return false;
        
        const earliestMonth = loadedMonths[0];
        const prevMonth = getPreviousMonth(earliestMonth);
        
        // Cannot go before 2025-09 (baseline month)
        return prevMonth >= '2025-09';
    })();

    // Check if remove buttons should be shown
    // Reactive computation based on loadedMonths and originalMonth
    $: canRemoveFuture = (() => {
        if (loadedMonths.length <= 1) return false;
        const latestMonth = loadedMonths[loadedMonths.length - 1];
        return latestMonth !== originalMonth;
    })();

    $: canRemovePast = (() => {
        if (loadedMonths.length <= 1) return false;
        const earliestMonth = loadedMonths[0];
        return earliestMonth !== originalMonth;
    })();

    // Helper: calculate invoice sum as sum of amount_due for selected classes
    function calculateInvoiceSum() {
        if (!invoiceData || !invoiceData.classes) {
            console.log('🔄 ADMIN DEBUG - calculateInvoiceSum: No invoice data or classes');
            return 0;
        }
        const sum = selectedClasses.reduce((sum, classId) => {
            const cls = invoiceData.classes.find(c => c.id === classId);
            // Ensure amount_due is always treated as a float number
            const amount = cls ? parseFloat(cls.amount_due) || 0.0 : 0.0;
            console.log(`🔄 ADMIN DEBUG - Class ${classId}: amount_due=${cls?.amount_due}, converted=${amount}`);
            return sum + amount;
        }, 0.0);
        console.log(`🔄 ADMIN DEBUG - Total invoice sum calculated: ${sum} for classes:`, selectedClasses);
        return Math.round(sum * 100) / 100; // Round to 2 decimal places to avoid floating point errors
    }

    // Update invoiceSum whenever selectedClasses changes
    $: {
        console.log('🔄 ADMIN DEBUG - Reactive update triggered. Selected classes:', selectedClasses);
        invoiceSum = calculateInvoiceSum();
        console.log('🔄 ADMIN DEBUG - Updated invoiceSum to:', invoiceSum);
    }

    async function handleSubmit(event) {
        event.preventDefault();
        if (selectedClasses.length === 0) {
            alert("Prašome pasirinkti nors vieną pamoką, kurią norėtumėte apmokėti.");
            return;
        }
        isCreatingPDF = true;
        await generateInvoiceFromClasses(selectedClasses);
        isCreatingPDF = false;
    }

    async function generateInvoiceFromClasses(selectedClasses) {
        const token = localStorage.getItem('jwtToken');
        if (!token) {
            console.error('No JWT token found. User might not be logged in.');
            return;
        }

        try {
            const response = await fetch('/studentplayground/admin/debug-invoice/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    class_ids: selectedClasses,
                    student_id: selectedStudentId // Pass student ID for admin debugging
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                email = data.email;
                const pdfBase64 = data.pdf_base64;
                pdfBase64V = pdfBase64;
                pdfUrl = `data:application/pdf;base64,${pdfBase64}`;
                console.log("PDF URL set:", pdfUrl);
            } else {
                const error = data;
                console.log('Error:', error);
                alert(`Error generating invoice: ${error.message || 'Unknown error'}`);
            }
        } catch (error) {
            console.log('Fetch error:', error);
        }
    }

    // Fetch payment link and redirect immediately (one-click payment) - DISABLED for admin
    async function fetchPaymentLinkAndRedirect() {
        alert("🚫 Payment functionality disabled in admin debug mode. This would normally redirect the student to payment.");
        return;
    }

    async function fetchInvoiceData() {
        if (!selectedStudentId) {
            console.error('No student selected for invoice data fetch');
            return;
        }

        // Default to current month on initial load
        if (loadedMonths.length === 0) {
            const currentMonth = getCurrentMonthString();
            originalMonth = currentMonth; // Track the original month
            await fetchMonthData(currentMonth, true); // Mark as initial load
        }
    }

    async function fetchMonthData(monthString, isInitialLoad = false) {
        if (!selectedStudentId) {
            console.error('No student selected for month data fetch');
            return;
        }

        const token = localStorage.getItem('jwtToken');
        if (!token) {
            console.error('No JWT token found. User might not be logged in.');
            errorForInvoiceData = 'Nepavyko rasti autentifikacijos duomenų. Prašome prisijungti iš naujo.';
            isLoadingMonth = false;
            return;
        }

        isLoadingMonth = true;
        if (isInitialLoad) {
            errorForInvoiceData = null;
        }

        try {
            // Build URL with month parameter - always fetch single month data
            const url = new URL(`/studentplayground/admin/debug-invoice-data/${selectedStudentId}/`);
            if (monthString) {
                url.searchParams.append('months', monthString);
            } else {
                url.searchParams.append('current_month_only', 'true');
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const result = await response.json();
                
                console.log('📊 ADMIN DEBUG - Month data loaded for student:', selectedStudentId, 'month:', monthString);
                console.log('📊 ADMIN DEBUG - Month data:', JSON.stringify(result, null, 2));
                
                // Store monthly data without affecting existing data
                if (monthString) {
                    monthlyData[monthString] = result;
                    if (!loadedMonths.includes(monthString)) {
                        loadedMonths = [...loadedMonths, monthString].sort();
                    }
                }
                
                // Only combine if this is initial load, otherwise append incrementally
                if (isInitialLoad) {
                    combineMonthlyData();
                } else {
                    appendMonthData(monthString, result);
                }
                
                errorForInvoiceData = null;
            } else {
                const error = await response.json();
                console.log('Error fetching month data:', error);
                if (isInitialLoad) {
                    errorForInvoiceData = error.message || 'Nepavyko gauti sąskaitos duomenų.';
                }
            }
        } catch (error) {
            console.log('Fetch error:', error);
            if (isInitialLoad) {
                errorForInvoiceData = 'Tinklo klaida. Patikrinkite interneto ryšį.';
            }
        } finally {
            isLoadingMonth = false;
        }
    }

    function combineMonthlyData() {
        if (Object.keys(monthlyData).length === 0) {
            invoiceData = null;
            return;
        }
        
        // Use the first month's balance and metadata
        const firstMonthKey = loadedMonths[0];
        const firstMonthData = monthlyData[firstMonthKey];
        
        if (!firstMonthData) {
            invoiceData = null;
            return;
        }
        
        // Combine classes from all loaded months
        let allClasses = [];
        for (const monthKey of loadedMonths) {
            const monthData = monthlyData[monthKey];
            if (monthData && monthData.classes) {
                allClasses = [...allClasses, ...monthData.classes];
            }
        }
        
        // Sort classes by date
        allClasses.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
        
        // Create combined invoice data
        invoiceData = {
            ...firstMonthData,
            classes: allClasses,
            loaded_months: loadedMonths,
            total_months_loaded: loadedMonths.length
        };
        
        console.log('📊 ADMIN DEBUG - Combined data from months:', loadedMonths);
        console.log('📊 ADMIN DEBUG - Total classes after combination:', allClasses.length);
    }

    function appendMonthData(monthString, newMonthData) {
        if (!invoiceData) {
            // If no existing data, just use the new data
            invoiceData = {
                ...newMonthData,
                loaded_months: [monthString],
                total_months_loaded: 1
            };
            console.log('📊 ADMIN DEBUG - Created initial invoice data for month:', monthString);
            return;
        }

        // Preserve existing data and append new classes
        const existingClasses = invoiceData.classes || [];
        const newClasses = newMonthData.classes || [];
        
        // Remove any duplicate classes (by ID) to avoid conflicts
        const existingClassIds = new Set(existingClasses.map(c => c.id));
        const uniqueNewClasses = newClasses.filter(c => !existingClassIds.has(c.id));
        
        // Combine and sort all classes
        const allClasses = [...existingClasses, ...uniqueNewClasses];
        allClasses.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
        
        // Update invoice data with new classes while preserving everything else
        invoiceData = {
            ...invoiceData, // Keep existing balance, payments, etc.
            classes: allClasses,
            loaded_months: loadedMonths,
            total_months_loaded: loadedMonths.length,
            // Optionally update metadata from the newest month
            last_updated_date: newMonthData.last_updated_date || invoiceData.last_updated_date
        };
        
        console.log('📊 ADMIN DEBUG - Appended month:', monthString);
        console.log('📊 ADMIN DEBUG - Added classes:', uniqueNewClasses.length);
        console.log('📊 ADMIN DEBUG - Total classes now:', allClasses.length);
        console.log('📊 ADMIN DEBUG - Loaded months:', loadedMonths);
    }

    async function addFutureMonth() {
        if (loadedMonths.length === 0) {
            console.error('No months loaded yet');
            return;
        }
        
        const latestMonth = loadedMonths[loadedMonths.length - 1];
        const nextMonth = getNextMonth(latestMonth);
        
        console.log('📊 ADMIN DEBUG - Adding future month:', nextMonth);
        await fetchMonthData(nextMonth);
    }

    async function addPastMonth() {
        if (loadedMonths.length === 0) {
            console.error('No months loaded yet');
            return;
        }
        
        const earliestMonth = loadedMonths[0];
        const prevMonth = getPreviousMonth(earliestMonth);
        
        // Check if we would go before the 2025-09 baseline
        if (prevMonth < '2025-09') {
            console.log('📊 ADMIN DEBUG - Cannot add past month:', prevMonth, '- before 2025-09 baseline');
            alert('Cannot load months before September 2025 (baseline month)');
            return;
        }
        
        console.log('📊 ADMIN DEBUG - Adding past month:', prevMonth);
        await fetchMonthData(prevMonth);
    }

    function removeFutureMonth() {
        if (loadedMonths.length <= 1) {
            console.error('Cannot remove the only month');
            return;
        }
        
        const latestMonth = loadedMonths[loadedMonths.length - 1];
        
        // Don't allow removing the original month
        if (latestMonth === originalMonth) {
            console.error('Cannot remove the original month');
            return;
        }
        
        console.log('📊 ADMIN DEBUG - Removing future month:', latestMonth);
        
        // Remove from loaded months
        loadedMonths = loadedMonths.slice(0, -1);
        
        // Remove from monthly data
        delete monthlyData[latestMonth];
        
        // Rebuild invoice data without the removed month
        rebuildInvoiceData();
    }

    function removePastMonth() {
        if (loadedMonths.length <= 1) {
            console.error('Cannot remove the only month');
            return;
        }
        
        const earliestMonth = loadedMonths[0];
        
        // Don't allow removing the original month
        if (earliestMonth === originalMonth) {
            console.error('Cannot remove the original month');
            return;
        }
        
        console.log('📊 ADMIN DEBUG - Removing past month:', earliestMonth);
        
        // Remove from loaded months
        loadedMonths = loadedMonths.slice(1);
        
        // Remove from monthly data
        delete monthlyData[earliestMonth];
        
        // Rebuild invoice data without the removed month
        rebuildInvoiceData();
    }

    function rebuildInvoiceData() {
        if (loadedMonths.length === 0) {
            invoiceData = null;
            return;
        }
        
        // Use the original month's data as base
        const baseMonthData = monthlyData[originalMonth];
        if (!baseMonthData) {
            console.error('Original month data not found');
            return;
        }
        
        // Combine classes from remaining loaded months
        let allClasses = [];
        for (const monthKey of loadedMonths) {
            const monthData = monthlyData[monthKey];
            if (monthData && monthData.classes) {
                allClasses = [...allClasses, ...monthData.classes];
            }
        }
        
        // Sort classes by date
        allClasses.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
        
        // Rebuild invoice data using original month as base
        invoiceData = {
            ...baseMonthData,
            classes: allClasses,
            loaded_months: loadedMonths,
            total_months_loaded: loadedMonths.length
        };
        
        console.log('📊 ADMIN DEBUG - Rebuilt data with months:', loadedMonths);
        console.log('📊 ADMIN DEBUG - Total classes after rebuild:', allClasses.length);
    }

    async function fetchAllStudents() {
        const token = localStorage.getItem('jwtToken');
        if (!token) {
            console.error('No JWT token found. User might not be logged in.');
            studentsError = 'Nepavyko rasti autentifikacijos duomenų.';
            isLoadingStudents = false;
            return;
        }

        try {
            const response = await fetch('/studentplayground/admin/get-all-students/', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const result = await response.json();
                allStudents = result.students || [];
                filteredStudents = allStudents;
                console.log('👥 ADMIN DEBUG - Loaded students:', allStudents.length);
                studentsError = null;
            } else {
                const error = await response.json();
                console.log('Error fetching students:', error);
                studentsError = error.message || 'Nepavyko gauti studentų sąrašo.';
            }
        } catch (error) {
            console.log('Fetch error:', error);
            studentsError = 'Tinklo klaida. Patikrinkite interneto ryšį.';
        } finally {
            isLoadingStudents = false;
        }
    }

    function selectStudent(student) {
        selectedStudent = student;
        selectedStudentId = student.id;
        studentQuery = `${student.first_name} ${student.last_name}`;
        showStudentDropdown = false;
        
        console.log('👤 ADMIN DEBUG - Selected student:', selectedStudent);
        
        // Reset invoice data when changing student
        invoiceData = null;
        errorForInvoiceData = null;
        selectedClasses = [];
        pdfUrl = '';
        
        // Reset monthly data
        loadedMonths = [];
        monthlyData = {};
        originalMonth = '';
        isLoadingMonth = false;
        
        // Fetch invoice data for selected student (starts with current month)
        fetchInvoiceData();
    }

    function filterStudents() {
        if (!studentQuery.trim()) {
            filteredStudents = allStudents;
            showStudentDropdown = false;
        } else {
            const query = studentQuery.toLowerCase();
            filteredStudents = allStudents.filter(student => 
                `${student.first_name} ${student.last_name}`.toLowerCase().includes(query) ||
                student.email.toLowerCase().includes(query)
            );
            showStudentDropdown = !selectedStudent && filteredStudents.length > 0;
        }
    }

    function clearStudentSelection() {
        selectedStudent = null;
        selectedStudentId = null;
        studentQuery = '';
        showStudentDropdown = false;
        filteredStudents = allStudents;
        
        // Reset invoice data
        invoiceData = null;
        errorForInvoiceData = null;
        selectedClasses = [];
        pdfUrl = '';
        
        // Reset monthly data
        loadedMonths = [];
        monthlyData = {};
        originalMonth = '';
        isLoadingMonth = false;
    }

    onMount(async () => {
        await fetchAllStudents();
    });

    // Reactive statement to filter students when search query changes
    $: if (studentQuery !== undefined) {
        filterStudents();
    }
</script>

<style>
    /* Import all styles from StandaloneInvoicesPage.svelte */
    button span {
         font-weight: bolder;
         font-size:larger;
     }
  
     button {
        margin-top:10px;
         box-sizing: border-box;
         display: flex;
         flex-direction: row;
         justify-content: center;
         align-items: center;
         padding: 12px 20px;
         gap: 10px;
     
         width: 100%;
         height: 60px;
     
         /* Purple background */
         background-color: #fab45a;
     
         /* Black border */
         border: 2px solid #000000;
     
         /* Box shadow */
         box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
     
         /* Rounded corners */
         border-radius: 8px;
     
         /* Inside auto layout */
         flex: none;
         order: 0;
         align-self: stretch;
         flex-grow: 0;
     
         /* Remove default button styling */
         outline: none;
         cursor: pointer;
   
         /* Remove the default button background on focus */
         background-image: none;
         transition: background-color 0.3s ease, color 0.3s ease;
     }
  
     /* Button hover effect */
     button:hover {
         background-color: #14695b;
         color:white;
     }
  
     /* Text inside the button */
     span {
        margin-top: 5px;
        margin-bottom: 5px;
        font-size: 1rem;
        font-weight: bold;
     
     }
   form {
       padding-bottom: 10px;
       padding-top:15px;
   }
   span {
       font-weight: bold;
   }
   
   label {
          font-size: 18px;
          font-weight: bold;
          margin-bottom: 5px;
          display: block;
          color:#14695b;
          text-align: center;
      }

      .balance-info {
          margin: 1rem 0 1.5rem 0;
          padding: 1rem;
          background-color: #f8f9fa;
          border-radius: 8px;
          border-left: 4px solid #14695b;
          text-align: center;
      }

      .balance-info > div {
          margin-bottom: 8px;
      }

      .balance-label {
          font-weight: 600;
          color: #14695b;
          display: inline;
      }

      .balance-amount {
          font-weight: 700;
          font-size: 1.1em;
          color: #333;
          margin-left: 8px;
      }

      .balance-date {
          color: #666;
          font-size: 0.9em;
          margin-left: 8px;
      }

      .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          margin: 2rem 0;
      }

      .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #14695b;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
      }

      @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
      }

      .loading-text {
          color: #666;
          font-size: 1rem;
          margin: 0;
      }

      .error-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          margin: 2rem 0;
          background-color: #fff3f3;
          border-radius: 8px;
          border-left: 4px solid #dc3545;
      }

      .error-text {
          color: #dc3545;
          font-size: 1rem;
          margin-bottom: 1rem;
          text-align: center;
      }

      .error-container button {
          background-color: #dc3545;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.9rem;
      }

      .error-container button:hover {
          background-color: #c82333;
      }

      .payments-section {
          margin-top: 2rem;
          padding-top: 2rem;
          border-top: 2px solid #e9ecef;
      }

      .payments-header {
          color: #14695b;
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
      }

      .payments-description {
          color: #666;
          font-size: 1rem;
          margin-bottom: 1.5rem;
          line-height: 1.4;
      }

      .no-payments-section {
          margin-top: 2rem;
          padding: 2rem;
          background-color: #f8f9fa;
          border-radius: 8px;
          text-align: center;
      }

      .no-payments-text {
          font-size: 1rem;
          color: #333;
      }

      /* Admin-specific styles */
      .admin-header {
          background-color: #dc3545;
          color: white;
          padding: 1rem;
          margin-bottom: 2rem;
          border-radius: 8px;
          text-align: center;
      }

      .admin-title {
          font-size: 1.5rem;
          font-weight: bold;
          margin: 0 0 0.5rem 0;
      }

      .admin-subtitle {
          font-size: 1rem;
          margin: 0;
          opacity: 0.9;
      }

      .student-selector {
          margin-bottom: 2rem;
          padding: 1.5rem;
          background-color: #f8f9fa;
          border-radius: 8px;
          border-left: 4px solid #dc3545;
      }

      .student-selector label {
          color: #dc3545;
          font-size: 1.2rem;
          margin-bottom: 1rem;
          display: block;
      }

      .input-container {
          position: relative;
          margin-top: 15px;
      }

      .search-input {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 0.9em;
          background-color: white;
          box-sizing: border-box;
      }

      .search-input:focus {
          outline: none;
          border-color: #14695b;
          box-shadow: 0 0 0 2px rgba(20, 105, 91, 0.2);
      }

      .autocomplete-list {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #ddd;
          border-top: none;
          border-radius: 0 0 6px 6px;
          max-height: 200px;
          overflow-y: auto;
          z-index: 1000;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      }

      .autocomplete-list-item {
          padding: 0.25rem 0.5rem;
          cursor: pointer;
          border-bottom: 1px solid #f0f0f0;
          transition: background-color 0.2s ease;
      }

      .autocomplete-list-item:hover {
          background-color: #f8f9fa;
      }

      .autocomplete-list-item:last-child {
          border-bottom: none;
      }

      .student-option {
          display: flex;
          flex-direction: column;
          gap: 0;
      }

      .student-name {
          font-weight: 500;
          color: #333;
          font-size: 0.8em;
          line-height: 1.1;
      }

      .student-email {
          color: #666;
          font-size: 0.7em;
          line-height: 1.1;
      }

      .student-id {
          color: #dc3545;
          font-size: 0.65em;
          font-weight: 500;
          line-height: 1.1;
      }

      .selected-student-badge {
          display: inline-flex;
          align-items: center;
          background-color: #d1ecf1;
          color: #0c5460;
          padding: 0.5rem 0.75rem;
          border-radius: 6px;
          font-size: 0.9rem;
          margin-top: 0.5rem;
      }

      .selected-student-badge .remove-selection {
          background: none;
          border: none;
          color: #0c5460;
          margin-left: 0.5rem;
          cursor: pointer;
          font-weight: bold;
          font-size: 1.2rem;
          padding: 0;
          width: auto;
          height: auto;
      }

      .selected-student-badge .remove-selection:hover {
          color: #062c33;
          background: none;
      }

      .form-help {
          font-size: 0.9rem;
          color: #666;
          margin-top: 0.5rem;
      }

      .selected-student-info {
          margin-top: 1rem;
          padding: 1rem;
          background-color: white;
          border-radius: 4px;
          border: 1px solid #ddd;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 15px;
      }

      .selected-student-details {
          flex: 1;
      }

      .student-info-label {
          font-weight: 600;
          color: #dc3545;
      }

      .btn-clear-selection {
          padding: 8px 16px;
          background-color: #6c757d;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.85em;
          white-space: nowrap;
          transition: background-color 0.2s;
      }

      .btn-clear-selection:hover {
          background-color: #545b62;
      }

      .debug-warning {
          background-color: #fff3cd;
          border: 1px solid #ffeaa7;
          border-left: 4px solid #f39c12;
          padding: 1rem;
          margin-bottom: 1rem;
          border-radius: 4px;
      }

      .debug-warning-title {
          font-weight: bold;
          color: #856404;
          margin-bottom: 0.5rem;
      }

      .debug-warning-text {
          color: #856404;
          margin: 0;
      }

      /* Loaded months info styles */
      .loaded-months-info {
          margin: 1rem 0;
          padding: 0.75rem;
          background-color: #f8f9fa;
          border-radius: 6px;
          border-left: 3px solid #14695b;
      }

      .loaded-months-header {
          font-weight: 600;
          color: #14695b;
          margin-bottom: 0.5rem;
          font-size: 0.95rem;
      }

      .loaded-months-list {
          font-size: 0.85rem;
          color: #666;
      }

      .loaded-month {
          display: inline-block;
          background-color: #14695b;
          color: white;
          padding: 3px 6px;
          margin: 1px;
          border-radius: 3px;
          font-size: 0.75rem;
      }

      .loaded-month.baseline-month {
          background-color: #ffc107;
          color: #000;
          border: 1px solid #f39c12;
          font-weight: 600;
      }

      /* Expand/collapse button styles */
      .expand-button-container {
          display: flex;
          justify-content: center;
          gap: 10px;
          margin: 1rem 0;
          flex-wrap: wrap;
      }

      .expand-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 20px;
          background-color: #e9ecef;
          color: #495057;
          border: 1px solid #ced4da;
          border-radius: 25px; /* Oval shape */
          cursor: pointer;
          font-size: 0.85rem;
          font-weight: 500;
          transition: all 0.2s ease;
          min-width: 120px;
          justify-content: center;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
      }

      .expand-btn:hover:not(:disabled) {
          background-color: #14695b;
          color: white;
          border-color: #14695b;
          transform: translateY(-1px);
          box-shadow: 0 2px 6px rgba(0,0,0,0.15);
      }

      .expand-btn:disabled {
          background-color: #f8f9fa;
          color: #adb5bd;
          border-color: #e9ecef;
          cursor: not-allowed;
          opacity: 0.6;
          transform: none;
          box-shadow: none;
      }

      .expand-btn.loading {
          background-color: #fff3cd;
          color: #856404;
          border-color: #ffeaa7;
      }

      .expand-text {
          font-size: 0.8rem;
          white-space: nowrap;
      }

      .expand-btn.remove-btn {
          background-color: #f8d7da;
          color: #721c24;
          border-color: #f5c6cb;
      }

      .expand-btn.remove-btn:hover:not(:disabled) {
          background-color: #dc3545;
          color: white;
          border-color: #dc3545;
      }
  </style>

<NavBarReusable label="Admin: Student Invoice Debug" backLink={backLinkUrl} />

<div class="admin-header">
    <h1 class="admin-title">🔧 Admin Debug: Student Invoice Viewer</h1>
    <p class="admin-subtitle">View any student's invoice page exactly as they see it</p>
</div>

<div class="debug-warning">
    <div class="debug-warning-title">⚠️ Debug Mode Active</div>
    <p class="debug-warning-text">This is an admin debugging tool. Payment functionality is disabled. You're seeing the exact view that the selected student would see.</p>
</div>

<!-- Student Selection -->
<div class="student-selector">
    <label for="student-search">Select Student to Debug:</label>
    
    {#if isLoadingStudents}
        <div class="loading-container">
            <div class="loading-spinner"></div>
            <p class="loading-text">Loading students...</p>
        </div>
    {:else if studentsError}
        <div class="error-container">
            <p class="error-text">Error: {studentsError}</p>
            <button on:click={() => { isLoadingStudents = true; studentsError = null; fetchAllStudents(); }}>
                Try Again
            </button>
        </div>
    {:else}
        <div class="input-container">
            <input 
                id="student-search"
                type="text" 
                placeholder="Search student by name or email..."
                bind:value={studentQuery}
                on:input={() => {
                    if (selectedStudent && studentQuery !== `${selectedStudent.first_name} ${selectedStudent.last_name}`) {
                        selectedStudent = null;
                        selectedStudentId = null;
                        showStudentDropdown = true;
                    } else if (!selectedStudent) {
                        showStudentDropdown = studentQuery.trim().length > 0;
                    }
                }}
                autocomplete="off"
                class="search-input"
            />
            
            {#if selectedStudent}
                <div class="selected-student-badge">
                    ✓ {selectedStudent.first_name} {selectedStudent.last_name} ({selectedStudent.email})
                    <button 
                        type="button" 
                        class="remove-selection"
                        on:click={() => {
                            selectedStudent = null;
                            selectedStudentId = null;
                            studentQuery = '';
                            showStudentDropdown = false;
                            filteredStudents = allStudents;
                            invoiceData = null;
                            errorForInvoiceData = null;
                            selectedClasses = [];
                            pdfUrl = '';
                            loadedMonths = [];
                            monthlyData = {};
                            originalMonth = '';
                            isLoadingMonth = false;
                        }}
                    >
                        ×
                    </button>
                </div>
            {/if}
            
            {#if showStudentDropdown}
                <div class="autocomplete-list">
                    {#each filteredStudents.slice(0, 8) as student}
                        <div 
                            class="autocomplete-list-item"
                            on:click={() => selectStudent(student)}
                            role="button"
                            tabindex="0"
                            on:keydown={(e) => (e.key === 'Enter' || e.key === ' ') && selectStudent(student)}
                        >
                            <div class="student-option">
                                <span class="student-name">{student.first_name} {student.last_name}</span>
                                <span class="student-email">{student.email}</span>
                                <span class="student-id">ID: {student.id}</span>
                            </div>
                        </div>
                    {/each}
                </div>
            {/if}
        </div>
        <div class="form-help">Start typing to search for students by name or email</div>
        
        {#if selectedStudent}
            <div class="selected-student-info">
                <div class="selected-student-details">
                    <div><span class="student-info-label">Name:</span> {selectedStudent.first_name} {selectedStudent.last_name}</div>
                    <div><span class="student-info-label">Email:</span> {selectedStudent.email}</div>
                    <div><span class="student-info-label">ID:</span> {selectedStudent.id}</div>
                </div>
                <button 
                    type="button" 
                    class="btn-clear-selection" 
                    on:click={clearStudentSelection}
                >
                    Clear Selection
                </button>
            </div>
        {/if}
    {/if}
</div>

<!-- Loaded Months Info (only show if student is selected and has data) -->
{#if selectedStudentId && loadedMonths.length > 0}
    <div class="loaded-months-info">
        <div class="loaded-months-header">📅 Loaded Months</div>
        <div class="loaded-months-list">
            {#each loadedMonths as month}
                <span class="loaded-month" class:baseline-month={month === '2025-09'}>
                    {formatMonthForDisplay(month)}
                    {#if month === '2025-09'}
                        <span style="font-size: 0.7em; opacity: 0.8;">📍</span>
                    {/if}
                </span>
            {/each}
        </div>
    </div>
{/if}

<!-- Student Invoice View (identical to StandaloneInvoicesPage.svelte) -->
{#if selectedStudentId}
    {#if isLoadingInvoiceData || isLoadingMonth}
        <div class="loading-container">
            <div class="loading-spinner"></div>
            <p class="loading-text">Loading student invoice data...</p>
        </div>
    {:else if errorForInvoiceData}
        <div class="error-container">
            <p class="error-text">Error: {errorForInvoiceData}</p>
            <button on:click={() => { isLoadingInvoiceData = true; errorForInvoiceData = null; fetchInvoiceData(); }}>
                Try Again
            </button>
        </div>
    {:else if invoiceData}
        <div class="balance-info">
            <div>
                <span class="balance-label">Student's Current Balance:</span>
                <span class="balance-amount">{invoiceData.balance} EUR</span>
            </div>
            <div>
                <span class="balance-label">Last Updated:</span>
                <span class="balance-date">{new Date(invoiceData.last_updated_date).toLocaleDateString('lt-LT')}</span>
            </div>
            {#if invoiceData.loaded_months && invoiceData.loaded_months.length > 0}
                <div>
                    <span class="balance-label">Showing months:</span>
                    <span class="balance-date">
                        {invoiceData.loaded_months.map(month => formatMonthForDisplay(month)).join(', ')}
                    </span>
                </div>
                <div>
                    <span class="balance-label">Total classes:</span>
                    <span class="balance-date">{invoiceData.classes.length}</span>
                </div>
            {/if}
            {#if invoiceData.snapshots_info && invoiceData.snapshots_info.total_snapshots > 0}
                <div style="margin-top: 0.5rem; padding: 0.5rem; background-color: #e8f5e8; border-radius: 4px;">
                    <span class="balance-label">📸 Snapshots created:</span>
                    <span class="balance-date">{invoiceData.snapshots_info.total_snapshots} month(s)</span>
                    {#if invoiceData.snapshots_info.snapshots_created && invoiceData.snapshots_info.snapshots_created.length > 0}
                        <div style="font-size: 0.8em; color: #666; margin-top: 4px;">
                            {invoiceData.snapshots_info.snapshots_created.map(month => formatMonthForDisplay(month)).join(', ')}
                        </div>
                    {/if}
                </div>
            {/if}
            {#if invoiceData.validation_info}
                <div style="margin-top: 1rem; font-size: 0.9em; color: #666;">
                    <div><strong>Debug Info:</strong></div>
                    <div>Valid Charges: €{invoiceData.validation_info.valid_charges_total}</div>
                    <div>Invalid Charges: €{invoiceData.validation_info.invalid_charges_total}</div>
                    <div>Total Payments: €{invoiceData.validation_info.total_payments}</div>
                    <div>Stored Balance: €{invoiceData.validation_info.stored_balance}</div>
                    <div>Validated Balance: €{invoiceData.validation_info.validated_balance}</div>
                </div>
            {/if}
        </div>

        {#if pdfUrl} <!-- Show PDF in iframe if URL exists -->
            <div>
                <iframe title="pdf_preview" src={pdfUrl} width="100%" height="600px"></iframe>
            </div>
           
            <div>
                <p>Total: {Math.round(invoiceSum)} EUR</p>
            
                <button
                    on:click={fetchPaymentLinkAndRedirect}
                    disabled={isFetchingPaymentLink}
                    style="background-color: #6c757d; cursor: not-allowed;"
                >
                    🚫 Payment Disabled (Debug Mode)
                </button>
            </div>
        {/if}

        <form on:submit|preventDefault={handleSubmit}>
            <label for="class-select">Select Classes (Student View):</label>
            
            <!-- Add/Remove Past Month Buttons (above classes) -->
            {#if loadedMonths.length > 0}
                <div class="expand-button-container">
                    <button 
                        type="button"
                        class="expand-btn" 
                        class:loading={isLoadingMonth}
                        disabled={isLoadingMonth || !canAddPast}
                        on:click={addPastMonth}
                        title={!canAddPast ? 'Cannot go before September 2025 (baseline month)' : 'Load previous month'}
                    >
                        <span class="expand-text">
                            {#if isLoadingMonth}
                                Kraunama...
                            {:else if !canAddPast}
                                Pasiekta pradžia
                            {:else}
                                Rodyti ankstesnį mėnesį
                            {/if}
                        </span>
                    </button>
                    
                    {#if canRemovePast}
                        <button 
                            type="button"
                            class="expand-btn remove-btn" 
                            disabled={isLoadingMonth}
                            on:click={removePastMonth}
                            title="Remove earliest month"
                        >
                            ❌ <span class="expand-text">Išimti ankščiausią</span>
                        </button>
                    {/if}
                </div>
            {/if}
            
            <CheckBoxAreForClassesComponent
                classes={invoiceData.classes}
                bind:selectedClasses
            />

            <!-- Add/Remove Future Month Buttons (below classes) -->
            {#if loadedMonths.length > 0}
                <div class="expand-button-container">
                    <button 
                        type="button"
                        class="expand-btn" 
                        class:loading={isLoadingMonth}
                        disabled={isLoadingMonth}
                        on:click={addFutureMonth}
                        title="Load next month"
                    >
                        <span class="expand-text">
                            {#if isLoadingMonth}
                                Kraunama...
                            {:else}
                                Rodyti vėlesnį mėnesį
                            {/if}
                        </span>
                    </button>
                    
                    {#if canRemoveFuture}
                        <button 
                            type="button"
                            class="expand-btn remove-btn" 
                            disabled={isLoadingMonth}
                            on:click={removeFutureMonth}
                            title="Remove latest month"
                        >
                            ❌ <span class="expand-text">Išimti vėliausią</span>
                        </button>
                    {/if}
                </div>
            {/if}

            <button type="submit">
                <span>{isCreatingPDF ? 'Creating...' : 'Generate Invoice (Debug)'}</span>
            </button>
        </form>

        {#if invoiceData.previous_payments.length > 0}
            <div class="payments-section">
                <h2 class="payments-header">Student's Previous Payments</h2>
                <p class="payments-description">Payment history as seen by the student.</p>
                <CompletedPaymentsList payments={invoiceData.previous_payments} />
            </div>
        {:else}
            <div class="no-payments-section">
                <h2 class="payments-header">Student's Previous Payments</h2>
                <p class="no-payments-text">This student has no previous payments.</p>
            </div>
        {/if}
    {/if}
{:else}
    <div class="loading-container">
        <p class="loading-text">👆 Please select a student above to view their invoice page</p>
    </div>
{/if}
